import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080/api/v1";
const WS_URL = import.meta.env.VITE_WS_URL || "ws://localhost:8080/ws";

const client = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add JWT token to all requests
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track whether a refresh is already in progress to avoid multiple simultaneous refreshes
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// Response interceptor to handle 401 errors and refresh tokens
client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh on 401 errors, not on login/refresh endpoints themselves
    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url === "/login" ||
      originalRequest.url === "/refresh"
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue the request while refresh is in progress
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      isRefreshing = false;
      processQueue(error, null);
      // Dispatch a custom event so AuthContext can handle logout
      window.dispatchEvent(new Event("auth:logout"));
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post(`${API_BASE}/refresh`, {
        refresh_token: refreshToken,
      });

      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("refresh_token", data.refresh_token);

      isRefreshing = false;
      processQueue(null, data.token);

      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      return client(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      processQueue(refreshError, null);

      // Refresh failed — clear tokens and force logout
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("auth_user");
      window.dispatchEvent(new Event("auth:logout"));

      return Promise.reject(refreshError);
    }
  }
);

const api = {
  // Authentication
  login: async (username_or_email: string, password: string) => {
    const { data } = await client.post("/login", {
      username_or_email,
      password,
    });
    return data;
  },

  // Workflows
  getWorkflows: async () => {
    const { data } = await client.get("/workflows");
    return data.workflows;
  },

  getWorkflowDetail: async (id: string) => {
    const { data } = await client.get(`/workflows/${id}`);
    return data;
  },

  createWorkflow: async (payload: any) => {
    const { data } = await client.post("/workflows", payload);
    return data;
  },

  retryWorkflow: async (id: string) => {
    const { data } = await client.post(`/workflows/${id}/retry`);
    return data;
  },

  cancelWorkflow: async (id: string) => {
    const { data } = await client.post(`/workflows/${id}/cancel`);
    return data;
  },

  deleteWorkflow: async (id: string) => {
    const { data } = await client.delete(`/workflows/${id}`);
    return data;
  },

  // Monitoring
  getAgentStatus: async () => {
    const { data } = await client.get("/agents/status");
    return data;
  },

  getServersStatus: async () => {
    const { data } = await client.get("/servers/status");
    return data;
  },

  // Health
  getHealth: async () => {
    const { data } = await client.get("/health");
    return data;
  },

  // Admin Accounts
  getAdminAccounts: async () => {
    const { data } = await client.get("/admin-accounts");
    // Backend returns { accounts: [...] }, so we need to extract the array
    return data.accounts || [];
  },

  // Parse spreadsheet to get domains
  parseSpreadsheet: async (spreadsheet_url: string) => {
    const { data } = await client.post("/workflows/parse-sheet", {
      spreadsheet_url,
    });
    return data;
  },

  // Create workflow from spreadsheet
  createWorkflowFromSheet: async (payload: {
    spreadsheet_url: string;
    admin_account: string;
    assignment_mode: string;
    domain_admin_mapping?: Record<string, string>;
  }) => {
    const { data } = await client.post("/workflows/from-sheet", payload);
    return data;
  },

  // SMS Verification
  submitSmsVerification: async (workflowId: string, payload: { account: string; code: string }) => {
    const { data } = await client.post(`/workflows/${workflowId}/sms-verification`, payload);
    return data;
  },

  // Workflow History
  getWorkflowHistory: async (params?: { customer_id?: string; status?: string; limit?: number; offset?: number }) => {
    const { data } = await client.get("/workflow-history", { params });
    return data;
  },

  getWorkflowHistoryDetails: async (id: string) => {
    const { data } = await client.get(`/workflow-history/${id}`);
    return data;
  },

  // WebSocket connection with proper reconnection handling
  connectWebSocket: (clientId: string, onMessage: (data: any) => void) => {
    let ws: WebSocket;
    let reconnectTimeout: NodeJS.Timeout | null = null;
    let isIntentionallyClosed = false;

    const connect = () => {
      // Get auth token for WebSocket connection
      const token = localStorage.getItem("auth_token");
      const wsUrl = token
        ? `${WS_URL}?client_id=${clientId}&token=${token}`
        : `${WS_URL}?client_id=${clientId}`;

      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        isIntentionallyClosed = false;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');

        // Only reconnect if not intentionally closed and not already reconnecting
        if (!isIntentionallyClosed && !reconnectTimeout) {
          reconnectTimeout = setTimeout(() => {
            reconnectTimeout = null;
            connect();
          }, 5000);
        }
      };
    };

    connect();

    // Return an object with the ws and a cleanup function
    return {
      get readyState() { return ws?.readyState || WebSocket.CLOSED; },
      send: (data: string) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(data);
        }
      },
      close: () => {
        isIntentionallyClosed = true;
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
        if (ws) {
          ws.close();
        }
      }
    };
  },
};

export default api;
